import type { ReactNode } from "react";

import { DotsThreeIcon } from "@phosphor-icons/react";
import { DropdownMenu, IconButton } from "@radix-ui/themes";

import type { DataTableDensity } from "~/v3-poc/components/DataTable";
import { WidgetOverflowMenuItems } from "~/v3-poc/components/Widget";

const DENSITY_OPTIONS: { label: string; value: DataTableDensity }[] = [
  { label: "Condensed", value: "condensed" },
  { label: "Normal", value: "normal" },
  { label: "Expanded", value: "expanded" },
];

export interface OrdersDisplayMenuItemsProps {
  density: DataTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
  onDensityChange: (density: DataTableDensity) => void;
  onHorizontalLinesChange: (enabled: boolean) => void;
  onVerticalLinesChange: (enabled: boolean) => void;
}

export const OrdersDisplayMenuItems = ({
  density,
  horizontalLines,
  verticalLines,
  onDensityChange,
  onHorizontalLinesChange,
  onVerticalLinesChange,
}: OrdersDisplayMenuItemsProps) => (
  <>
    <DropdownMenu.CheckboxItem
      checked={horizontalLines}
      onSelect={(event) => event.preventDefault()}
      onCheckedChange={(checked) => onHorizontalLinesChange(checked === true)}
    >
      Horizontal lines
    </DropdownMenu.CheckboxItem>
    <DropdownMenu.CheckboxItem
      checked={verticalLines}
      onSelect={(event) => event.preventDefault()}
      onCheckedChange={(checked) => onVerticalLinesChange(checked === true)}
    >
      Vertical lines
    </DropdownMenu.CheckboxItem>
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>Row spacing</DropdownMenu.SubTrigger>
      <DropdownMenu.SubContent>
        <DropdownMenu.RadioGroup
          value={density}
          onValueChange={(value) => {
            if (
              value === "condensed" ||
              value === "normal" ||
              value === "expanded"
            ) {
              onDensityChange(value);
            }
          }}
        >
          {DENSITY_OPTIONS.map((option) => (
            <DropdownMenu.RadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.SubContent>
    </DropdownMenu.Sub>
  </>
);

export interface OrdersDisplayMenuPresentationProps {
  items: ReactNode;
  isOverflow: boolean;
}

export const OrdersDisplayMenuPresentation = ({
  items,
  isOverflow,
}: OrdersDisplayMenuPresentationProps) => {
  if (isOverflow) {
    return <WidgetOverflowMenuItems>{items}</WidgetOverflowMenuItems>;
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <IconButton
          type="button"
          size="1"
          variant="ghost"
          color="gray"
          aria-label="Table display options"
        >
          <DotsThreeIcon size={16} weight="bold" aria-hidden />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content size="1" align="end" variant="soft">
        {items}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
